import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, IMAGES } from "@/lib/api";
import { Logo, Wordmark } from "@/components/site/Logo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/admin", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-[#050505] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:border-[#F5B841] focus:outline-none focus:ring-1 focus:ring-[#F5B841] transition-colors";

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#050505]" data-testid="admin-login-page">
      <div className="relative hidden md:block overflow-hidden border-r border-white/10">
        <img src={IMAGES.hero} alt="stage" className="w-full h-full object-cover grayscale opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
        <div className="absolute bottom-12 left-12">
          <h2 className="font-display text-4xl tracking-tighter max-w-xs">Production control room.</h2>
          <p className="text-white/50 mt-3 max-w-xs">Manage every incoming quote request in one place.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-sm mb-10">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <div className="flex items-center gap-3 mb-8">
            <Logo />
            <Wordmark />
          </div>
          <h1 className="font-display text-3xl tracking-tight">Admin Login</h1>
          <p className="text-white/45 text-sm mt-2 mb-8">Sign in to view booking requests.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <input data-testid="login-email" type="email" className={field} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input data-testid="login-password" type="password" className={field} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#F5B841] text-black font-semibold py-3 hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
